/*
 * The fleet's search input (the reference CMS design @ `c4387239`), with three deviations,
 * all named here because the file otherwise reads as a copy:
 *
 *  - the placeholder and the clear button's `aria-label` are English. They are
 *    Czech literals in the fleet, which is localised through i18next; this
 *    console is not, and its content is English (the label is the one a screen
 *    reader speaks, so leaving it was shipping a Czech word to an English UI).
 *  - the timer's type is `ReturnType<typeof setTimeout>` rather than
 *    `NodeJS.Timeout`: this package is typed against the DOM, where that name
 *    does not exist.
 *
 * Everything else is byte for byte, so the next copy from the fleet has only
 * these three lines to reconcile.
 */
import * as React from 'react'
import { XIcon } from 'lucide-react'
import { cn } from '../../lib/utils'
import { Input } from './input'

interface SearchInputProps extends Omit<React.ComponentProps<'input'>, 'type' | 'onChange'> {
  value: string
  onChange: (value: string) => void
  onClear?: () => void
  debounceMs?: number
}

export function SearchInput({
  value,
  onChange,
  onClear,
  debounceMs = 300,
  className,
  placeholder = 'Search…',
  ...props
}: SearchInputProps) {
  const [internalValue, setInternalValue] = React.useState(value)
  const timeoutRef = React.useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  // Sync internal value when external value changes
  React.useEffect(() => {
    setInternalValue(value)
  }, [value])

  // Debounced onChange
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    setInternalValue(newValue)

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    timeoutRef.current = setTimeout(() => {
      onChange(newValue)
    }, debounceMs)
  }

  const handleClear = () => {
    setInternalValue('')
    onChange('')
    onClear?.()
  }

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  return (
    <div className="relative">
      <Input
        type="text"
        value={internalValue}
        onChange={handleChange}
        className={cn('pr-9', className)}
        placeholder={placeholder}
        {...props}
      />
      {internalValue && (
        <button
          type="button"
          onClick={handleClear}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
          aria-label="Clear search"
        >
          <XIcon className="h-4 w-4" />
        </button>
      )}
    </div>
  )
}
